import { EventEmitter } from 'events';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { getDatabase, goOffline, goOnline, onValue, ref } from 'firebase/database';
import { logPOSEvent } from './backgroundLogger';
import { app as firebaseApp, db } from '../firebase/config';

/**
 * Connection Monitor Service
 * Monitors internet connectivity using Electron's net module (main process) or browser APIs (web mode)
 * Provides connection status state to React components
 */
class ConnectionMonitor extends EventEmitter {
  private browserOnline: boolean = typeof navigator === 'undefined' ? true : navigator.onLine;
  private electronOnline: boolean = true;
  private isOnline: boolean = this.browserOnline;
  private isElectronMode: boolean = false;
  private desktopProbe = false;
  private firebaseWatch = false;
  private firebaseConnected = false;
  private firebaseEverConnected = false;
  private forcedOffline = false;
  private networkPause: Promise<void> = Promise.resolve();

  constructor() {
    super();
    this.initialize();
  }

  private initialize() {
    // Check if running in Electron
    this.isElectronMode = typeof window !== 'undefined' && window.api !== undefined;

    window.addEventListener('online', () => this.setBrowserOnline(true));
    window.addEventListener('offline', () => this.setBrowserOnline(false));

    if (this.isElectronMode) {
      this.initializeElectronMode();
    } else {
      this.publishStatus();
    }
  }

  private combinedOnline(): boolean {
    if (this.forcedOffline) return false;
    if (!this.browserOnline) return false;
    if (this.firebaseEverConnected) return this.firebaseConnected;
    if (this.desktopProbe) return this.electronOnline;
    return this.electronOnline;
  }

  /**
   * Realtime Database socket. .info/connected is not stored data.
   * It is true only while this screen can reach Firebase.
   * This watch never pauses Firestore. Persistence queues writes on its own.
   */
  public startFirebaseConnectionWatch(): void {
    if (this.firebaseWatch) return;
    this.firebaseWatch = true;
    const connectedRef = ref(getDatabase(firebaseApp), '.info/connected');
    onValue(connectedRef, (snap) => {
      const connected = snap.val() === true;
      console.log('[ConnectionMonitor] Firebase socket:', connected ? 'Online' : 'Offline');
      if (connected) {
        this.firebaseEverConnected = true;
        this.firebaseConnected = true;
        this.publishStatus();
        return;
      }
      if (this.firebaseEverConnected) {
        this.firebaseConnected = false;
        this.publishStatus();
      }
    });
  }

  private setBrowserOnline(online: boolean) {
    this.browserOnline = online;
    console.log('[ConnectionMonitor] Browser network:', online ? 'Online' : 'Offline');
    this.publishStatus();
  }

  private publishStatus() {
    const online = this.combinedOnline();
    if (online && !this.isOnline) {
      this.handleOnline();
    } else if (!online && this.isOnline) {
      this.handleOffline();
    }
  }

  /**
   * Initialize connection monitoring in Electron mode
   * Uses Electron's net module via IPC (more reliable than navigator.onLine)
   */
  private async initializeElectronMode() {
    console.log('[ConnectionMonitor] Initializing in Electron mode');

    // Get initial status from main process
    try {
      const status = await window.api.invoke('get-network-status');
      this.electronOnline = status.isOnline;
      console.log('[ConnectionMonitor] Initial network status:', this.electronOnline ? 'Online' : 'Offline');
    } catch (error) {
      console.error('[ConnectionMonitor] Error getting initial network status:', error);
      this.electronOnline = navigator.onLine;
    }
    this.publishStatus();

    if (window.api && window.api.on) {
      window.api.on('network-status-changed', (_event: any, data: { isOnline: boolean }) => {
        console.log('[ConnectionMonitor] Network status changed:', data.isOnline ? 'Online' : 'Offline');
        this.electronOnline = data.isOnline;
        this.publishStatus();
      });
    }
  }

  private async handleOnline() {
    if (!this.isOnline) {
      this.isOnline = true;
      this.emit('online');
      console.log('[ConnectionMonitor] Connection restored');
      await logPOSEvent('CONNECTION_STATUS', { status: 'Online', timestamp: Date.now() });
    }
  }

  private async handleOffline() {
    if (this.isOnline) {
      this.isOnline = false;
      this.emit('offline');
      console.log('[ConnectionMonitor] Connection lost');
      await logPOSEvent('CONNECTION_STATUS', { status: 'Offline', timestamp: Date.now() });
    }
  }

  public getStatus(): boolean {
    return this.isOnline;
  }

  public isNetworkDisabled(): boolean {
    return this.forcedOffline;
  }

  /**
   * Test pause for a DEV or ESSAI certificate.
   * The flag drops first, so order writes take the offline path immediately.
   * Firestore and the Realtime Database socket follow that flag.
   * Enable restores both, then clears the flag.
   */
  public setNetworkDisabled(disabled: boolean): Promise<void> {
    const run = this.networkPause.then(() => this.applyNetworkPause(disabled));
    this.networkPause = run.then(() => undefined, () => undefined);
    return run;
  }

  private async applyNetworkPause(disabled: boolean): Promise<void> {
    const database = getDatabase(firebaseApp);
    if (disabled) {
      if (this.forcedOffline) return;
      this.forcedOffline = true;
      this.publishStatus();
      this.emit('network-test', true);
      try {
        await disableNetwork(db);
        goOffline(database);
      } catch (error) {
        console.error('[ConnectionMonitor] Network pause failed', error);
        throw error;
      }
      return;
    }

    if (!this.forcedOffline) return;
    try {
      await enableNetwork(db);
      goOnline(database);
    } catch (error) {
      console.error('[ConnectionMonitor] Network restore failed', error);
      throw error;
    }
    this.forcedOffline = false;
    this.publishStatus();
    this.emit('network-test', false);
  }

  public async waitForConnection(timeout: number = 30000): Promise<boolean> {
    if (this.isOnline) {
      return true;
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        this.removeListener('online', onOnline);
        resolve(false);
      }, timeout);

      const onOnline = () => {
        clearTimeout(timeoutId);
        this.removeListener('online', onOnline);
        resolve(true);
      };

      this.once('online', onOnline);
    });
  }

  public destroy() {
    window.removeEventListener('online', () => this.handleOnline());
    window.removeEventListener('offline', () => this.handleOffline());
    this.removeAllListeners();
  }
}

// Singleton instance
const connectionMonitor = new ConnectionMonitor();

export default connectionMonitor;

